import {
    BlurEvent, Component, DropdownItem, DropdownSelectionEvent, LookupModelSearchEvent, LookupModelSelectionEvent,
    Switch, TableRow, TableRowMode, Textbox
} from "@mcleod/components";
import { ModelRow, StringUtil } from "@mcleod/core";
import { AssetUtil, LMECompanySettings } from "@mcleod/general";
import { AutogenLayoutProhibitedTable } from "./autogen/AutogenLayoutProhibitedTable";

export class ProhibitedTable extends AutogenLayoutProhibitedTable {
    brokerage_types: DropdownItem[] = [
        new DropdownItem("C", "Carrier"),
        new DropdownItem("D", "Carrier Driver")
    ];
    asset_types: DropdownItem[] = [
        new DropdownItem("M", "Company Driver"),
        new DropdownItem("O", "Owner Operator")
    ];
    both_types: DropdownItem[] = [ ...this.brokerage_types, ...this.asset_types ];

    override onLoad() {
        const company: LMECompanySettings = LMECompanySettings.getSingleton();
        if (company.isBrokerageLtl() == false) {
            this.tableProhibited.removeColumn("fromLane");
            this.tableProhibited.removeColumn("toLane");
            this.tableProhibited.removeColumn("bidirectional");
        }
    }

    tableProhibitedOnRowDisplay(event) {
        const tableRow = event.target as TableRow;
        const modelRow: ModelRow = tableRow.data;
        const company = LMECompanySettings.getSingleton();
        const textboxProhibitDriverName = tableRow.findComponentById("textboxProhibitDriverName") as Textbox;
        const textboxProhibitRowId = tableRow.findComponentById("textboxProhibitRowId") as Textbox;
        const textboxProhibitedRowType = tableRow.findComponentById("textboxProhibitedRowType") as Textbox;
        const textboxFromRegion = tableRow.findComponentById("textboxFromRegionUid") as Textbox;
        const textboxToRegion = tableRow.findComponentById("textboxToRegionUid") as Textbox;
        const switchBidirectionalLane = tableRow.findComponentById("switchBidirectionalLane") as Switch;
        const isBrokerageLtl = LMECompanySettings.getSingleton().isBrokerageLtl() == true

        const isDriverType = "D" === tableRow.data?.get("prohibited_row_type");
        const isAssetType = "M" === tableRow.data?.get("prohibited_row_type")
        || "O" === tableRow.data?.get("prohibited_row_type");

        if (company.isBoth())
        {
            textboxProhibitRowId.addLookupModelSelectionListener((event: LookupModelSelectionEvent) => this.textboxProhibitedRowIdOnLookupModelSelection(event));
            textboxProhibitDriverName.enabled = isDriverType || isAssetType;
            textboxProhibitDriverName.required = isDriverType || isAssetType;
            textboxProhibitRowId.enabled = !isAssetType;
            textboxProhibitRowId.required = !isAssetType;
            textboxProhibitedRowType.items = this.both_types;
        }
        else if (company.isAsset())
        {
            textboxProhibitDriverName.enabled = true;
            textboxProhibitDriverName.required = true;
            textboxProhibitRowId.enabled = false;
            textboxProhibitRowId.required = false;
            textboxProhibitedRowType.items = this.asset_types;
        }
        else
        {
            textboxProhibitRowId.addLookupModelSelectionListener((event: LookupModelSelectionEvent) => this.textboxProhibitedRowIdOnLookupModelSelection(event));
            textboxProhibitDriverName.enabled = isDriverType;
            textboxProhibitDriverName.required = isDriverType;
            textboxProhibitRowId.enabled = true;
            textboxProhibitRowId.required = true;
            textboxProhibitedRowType.items = this.brokerage_types;
        }

        textboxProhibitedRowType.addAfterDropdownSelectionListener((event: DropdownSelectionEvent) => this.textboxProhibitedRowTypeOnDropdownSelection(event));
        this.setupDriverLookup(company, textboxProhibitDriverName, isAssetType, event);
        this.setupRowTypeSelection(textboxProhibitedRowType, tableRow.data?.get("prohibited_row_type"),
            TableRowMode.QUICK_ADD === tableRow.mode);

        if (TableRowMode.QUICK_ADD === tableRow.mode) {
            tableRow.findComponentById("labelUserName").visible = false;
            tableRow.findComponentById("labelProhibitEnterDate").visible = false;
        }

        if (isBrokerageLtl) {
            textboxFromRegion.enabled = !isDriverType;
            textboxToRegion.enabled = !isDriverType;
            switchBidirectionalLane.enabled = !isDriverType;
            if (modelRow.get("from_region_uid") != null)
                textboxFromRegion.text = modelRow.get("from_region")
            if (modelRow.get("to_region_uid") != null)
                textboxToRegion.text = modelRow.get("to_region")

            textboxFromRegion.addLookupModelSelectionListener(this.textboxFromRegionOnLookupModelSelection);
            textboxToRegion.addLookupModelSelectionListener(this.textboxToRegionOnLookupModelSelection);
        }
    }

    private textboxProhibitedRowTypeOnDropdownSelection(event: DropdownSelectionEvent) {
        const tableRow = TableRow.getContainingTableRow(event.target);
        const company: LMECompanySettings = LMECompanySettings.getSingleton();
        const assetCompany = company.isAsset() && !company.isBrokerage();
        const modelRow = tableRow.data;
        const textboxProhibitDriverName = tableRow.findComponentById("textboxProhibitDriverName") as Textbox;
        const textboxProhibitCarrier = tableRow.findComponentById("textboxProhibitRowId") as Textbox;
        const carrierType = "C" == event.selection.value;

        const isBrokerageLtl = LMECompanySettings.getSingleton().isBrokerageLtl() == true
        if (isBrokerageLtl) {
            const textboxFromRegion = tableRow.findComponentById("textboxFromRegionUid") as Textbox;
            const textboxToRegion = tableRow.findComponentById("textboxToRegionUid") as Textbox;
            const switchBidirectionalLane = tableRow.findComponentById("switchBidirectionalLane") as Switch;
            textboxFromRegion.enabled = carrierType;
            textboxToRegion.enabled = carrierType;
            switchBidirectionalLane.enabled = carrierType;
            if (!carrierType)
                this.clearLaneTextboxes(modelRow, textboxFromRegion, textboxToRegion);
            this.setBidirectionalLane(switchBidirectionalLane, carrierType, tableRow.data?.get("bidirectional_lane"));
        }
        if (assetCompany)
        {
            textboxProhibitDriverName.clear();
        }
        else
        {
            this.checkIfTypeChanged(textboxProhibitDriverName, textboxProhibitCarrier, event);
        }
    }

    checkIfTypeChanged(textboxDriver: Textbox, textboxCarrier: Textbox, event: DropdownSelectionEvent)
    {
        const currentLookup = textboxDriver.lookupModel;

        if ((event.selection.value == "O" || event.selection.value == "M") && event.userInitiatedChange)
        {
            if (currentLookup == "lme/powerbroker/carrier-driver") {
                textboxDriver.setProps(AssetUtil.getDriverLookupModelProps());
            }
            textboxDriver.clear();
            textboxCarrier.clear();
            textboxCarrier.enabled = false;
            textboxCarrier.required = false;
            textboxDriver.enabled = true;
            textboxDriver.required = true;
        }
        else if ((event.selection.value == "C" || event.selection.value == "D") && event.userInitiatedChange)
        {
            if (currentLookup == "lme/dispatch/asset/driver-only") {
                textboxDriver.lookupModel = "lme/powerbroker/carrier-driver";
                textboxDriver.lookupModelLayout = "lme/powerbroker/CarrierDriverLookup";
                textboxDriver.lookupModelResultField = "driver_name";
                textboxDriver.lookupModelDisplayField = "driver_name";
            }
            textboxDriver.clear();
            textboxCarrier.clear();
            textboxCarrier.enabled = true;
            textboxCarrier.required = true;
            textboxDriver.enabled = false;
            textboxDriver.required = false;
        }
    }

    private textboxProhibitedRowIdOnLookupModelSelection(event: LookupModelSelectionEvent) {
        if (event.hasSelection() === true) {
            const tableRow = TableRow.getContainingTableRow(event.target);
            const textboxProhibitDriverName = tableRow.findComponentById("textboxProhibitDriverName") as Textbox;
            this.setDriverTextboxEnabled(textboxProhibitDriverName, "D" === tableRow.data?.get("prohibited_row_type"));
        }
    }

    private textboxFromRegionOnLookupModelSelection(event: LookupModelSelectionEvent) {
        if (event.hasSelection() === true) {
            const modelRow = TableRow.getContainingTableRow(event.target)?.data;
            if (modelRow != null)
                modelRow.set("from_region", event.target.text)
        }
    }

    private textboxToRegionOnLookupModelSelection(event: LookupModelSelectionEvent) {
        if (event.hasSelection() === true) {
            const modelRow = TableRow.getContainingTableRow(event.target)?.data;
            if (modelRow != null)
                modelRow.set("to_region", event.target.text)
        }
    }

    setDriverTextboxEnabled(textboxProhibitDriverName: Textbox, enable: boolean) {
        textboxProhibitDriverName.enabled = enable;
        textboxProhibitDriverName.required = enable;
        textboxProhibitDriverName.text = null;
    }

    setupRowTypeSelection(textboxProhibitRowType: Textbox, type: string, addMode: boolean) {
        if (addMode) {
            const company = LMECompanySettings.getSingleton();
            if (company.isBoth() || company.isBrokerage())
                textboxProhibitRowType.setSelectedItemFromValue("C");
            else
                textboxProhibitRowType.setSelectedItemFromValue("M");
        }
        else
            textboxProhibitRowType.setSelectedItemFromValue(type);
    }

    textboxProhibitDriverNameBeforeLookupModelSearch(event: LookupModelSearchEvent) {
        const tableRow = TableRow.getContainingTableRow(event.target as Component);

        if ("M" == tableRow.data?.get("prohibited_row_type")) {
            event.filter.type_of = "C";
        }
        else if ("O" == tableRow.data?.get("prohibited_row_type")) {
            event.filter.type_of = "O";
        }
        else if (tableRow.data?.get("prohibited_row_id") != null) {
            event.filter.carrier_id = tableRow.data?.get("prohibited_row_id");
        }
        else {
            event.preventDefault();
        }
    }

    textboxProhibitRowIdOnBlur(event: BlurEvent) {
        const textboxCommodityId = event.target as Textbox;
        if (event.changedWhileFocused && StringUtil.isEmptyString(textboxCommodityId.text)) {
            const textboxProhibitDriverName = TableRow.getContainingTableRow(textboxCommodityId).findComponentById("textboxProhibitDriverName") as Textbox;
            this.setDriverTextboxEnabled(textboxProhibitDriverName, false);
        }
    }

    setBidirectionalLane(switchBidirectionalLane: Switch, allowed: boolean, bidirectional: String) {
        switchBidirectionalLane.enabled = allowed;
        if (allowed) {
            switchBidirectionalLane.checked = "Y" === bidirectional;
            switchBidirectionalLane.enabled = true;
        }
        else {
            switchBidirectionalLane.checked = switchBidirectionalLane.enabled = false;
        }
    }

    clearLaneTextboxes(modelRow, textboxFromRegion: Textbox, textboxToRegion: Textbox) {
        modelRow.set("from_region", null);
        modelRow.set("from_region_uid", null);
        textboxFromRegion.clear();
        modelRow.set("to_region", null);
        modelRow.set("to_region_uid", null);
        textboxToRegion.clear();
    }

    setupDriverLookup(company: LMECompanySettings, textboxDriver: Textbox, isAssetType: boolean, event)
    {
        if ((company.isAsset() && !company.isBrokerage()) || (company.isBoth() && isAssetType))
        {
            textboxDriver.setProps(AssetUtil.getDriverLookupModelProps());
            const modelRow = textboxDriver.boundRow;
            const lmData = modelRow?.getFirstLookupModelData(textboxDriver.field);
            if (modelRow && lmData) {
                const first_name = lmData.get("first_name") ?? "";
                const name = lmData.get("name") ?? "";
                modelRow.setLookupModelData(textboxDriver.field, {
                    first_name,
                    name,
                    full_name: first_name + " " + name
                });
                textboxDriver.displayData(modelRow, null, 0);
            }
        }
    }
}
